import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Camera, Upload, Loader2, AlertTriangle, Check, X, Fuel, Zap, Calendar } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

/**
 * AI-Powered Receipt Scanner
 * Extracts Category, Start Date, End Date, and Quantity from receipts using OpenAI Vision via Edge Function
 */
const ReceiptScanner = ({ onDataExtracted, category = 'fuel', onClose }) => {
  const [scanning, setScanning] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [lowConfidence, setLowConfidence] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a JPG, PNG, or WebP image');
      return;
    }

    // Create preview
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setScanning(true);
    setLowConfidence(false);

    try {
      // 1. Convert to Base64
      const base64Str = await fileToBase64(file);

      // 2. Call Edge Function
      const { data, error } = await supabase.functions.invoke('analyze-receipt', {
        body: { imageBase64: base64Str.split(',')[1] } // remove data:image/... base64 prefix
      });

      if (error) throw error;

      console.log('AI Extraction Result:', data);

      const structuredData = {
        category: data.category || category,
        start_date: data.start_date || null,
        end_date: data.end_date || null,
        quantity: data.quantity || null,
        unit: data.unit || (category === 'electricity' ? 'kWh' : 'L'),
        confidence: data.confidence_score || 0
      };

      setExtractedData(structuredData);

      // Check confidence level
      if (structuredData.confidence < 70 || !structuredData.quantity || !structuredData.start_date) {
        setLowConfidence(true);
        toast.info('Low confidence - please verify the extracted data carefully.');
      } else {
        toast.success('Receipt scanned successfully!');
      }

    } catch (error) {
      console.error('AI Scan Error:', error);
      toast.error('Failed to analyze receipt. Please try again or enter manually.');
      setPreviewUrl(null);
    } finally {
      setScanning(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleConfirm = () => {
    if (extractedData && onDataExtracted) {
      onDataExtracted({
        start_date: extractedData.start_date,
        end_date: extractedData.end_date,
        quantity: extractedData.quantity,
        unit: extractedData.unit,
        category: extractedData.category // Optional, depends if form supports auto-switching category
      });
      toast.success('Data applied to form');
      onClose?.();
    }
  };

  const handleManualEdit = (field, value) => {
    setExtractedData(prev => ({
      ...prev,
      [field]: field === 'quantity' ? parseFloat(value) || null : value
    }));
  };

  const CategoryIcon = category === 'electricity' ? Zap : Fuel;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#003B5C] to-[#74A57F] rounded-lg flex items-center justify-center shadow-inner">
                <Camera className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  AI Receipt Scanner
                </h2>
                <p className="text-sm text-slate-500">
                  Scan a {category === 'electricity' ? 'power bill' : 'fuel receipt'}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} disabled={scanning}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          {/* Upload Options */}
          {!previewUrl && !scanning && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 mb-4">
                Take a photo or upload an image of your receipt. Our AI Co-Pilot will instantly extract the date limits and consumption quantities.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {/* Camera Capture */}
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="p-6 border-2 border-dashed border-slate-300 rounded-xl hover:border-[#74A57F] hover:bg-[#74A57F]/5 transition-all text-center group"
                >
                  <Camera className="w-10 h-10 mx-auto text-slate-400 group-hover:text-[#74A57F] mb-2" />
                  <span className="text-sm font-medium text-slate-600">Take Photo</span>
                </button>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />

                {/* File Upload */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-6 border-2 border-dashed border-slate-300 rounded-xl hover:border-[#74A57F] hover:bg-[#74A57F]/5 transition-all text-center group"
                >
                  <Upload className="w-10 h-10 mx-auto text-slate-400 group-hover:text-[#74A57F] mb-2" />
                  <span className="text-sm font-medium text-slate-600">Upload Image</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mt-4 text-xs text-slate-500">
                <strong>Tips for best results:</strong>
                <br />• Ensure good lighting and crisp focus
                <br />• Include the full billing period dates if scanning a power or gas bill
                <br />• Make sure the consumption units are clearly visible
              </div>
            </div>
          )}

          {/* Scanning Progress */}
          {scanning && (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-[#003B5C] mx-auto mb-4" />
              <p className="text-lg font-medium text-[#003B5C] mb-2">AI is analyzing receipt...</p>
              <p className="text-sm text-slate-500">Extracting dates and consumption data</p>
            </div>
          )}

          {/* Results */}
          {extractedData && !scanning && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4">
              {/* Preview Image */}
              {previewUrl && (
                <div className="relative rounded-lg overflow-hidden border border-slate-200 mb-6 bg-slate-50 flex justify-center max-h-48">
                  <img
                    src={previewUrl}
                    alt="Receipt preview"
                    className="object-contain w-full h-full"
                  />
                  <button
                    onClick={() => {
                      setPreviewUrl(null);
                      setExtractedData(null);
                    }}
                    className="absolute top-2 right-2 bg-white/90 rounded-full p-1 hover:bg-white shadow"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Low Confidence Warning */}
              {lowConfidence && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3 mb-4">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Review carefully</p>
                    <p className="text-xs text-amber-700">
                      We had trouble extracting some details. Please verify the yellow fields.
                    </p>
                  </div>
                </div>
              )}

              {/* Extracted Data Form */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-1">
                  <Label className="text-[#003B5C] flex items-center mb-1 text-xs">
                    <Calendar className="w-3.5 h-3.5 mr-1.5" /> Start Date
                  </Label>
                  <Input
                    type="date"
                    value={extractedData.start_date || ''}
                    onChange={(e) => handleManualEdit('start_date', e.target.value)}
                    className={!extractedData.start_date ? 'border-amber-300 bg-amber-50' : ''}
                  />
                </div>
                <div className="col-span-1">
                  <Label className="text-[#003B5C] flex items-center mb-1 text-xs">
                    <Calendar className="w-3.5 h-3.5 mr-1.5" /> End Date (Optional)
                  </Label>
                  <Input
                    type="date"
                    value={extractedData.end_date || ''}
                    onChange={(e) => handleManualEdit('end_date', e.target.value)}
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-[#003B5C] flex items-center mb-1 text-xs">
                    <CategoryIcon className="w-3.5 h-3.5 mr-1.5" />
                    Quantity Consumed
                  </Label>
                  <div className="flex">
                    <Input
                      type="number"
                      step="0.01"
                      value={extractedData.quantity || ''}
                      onChange={(e) => handleManualEdit('quantity', e.target.value)}
                      className={`rounded-r-none ${!extractedData.quantity ? 'border-amber-300 bg-amber-50' : ''}`}
                      placeholder="e.g. 154.2"
                    />
                    <div className="flex items-center px-4 bg-slate-100 border border-l-0 border-slate-200 rounded-r-md text-sm text-slate-600 font-medium">
                      {extractedData.unit || 'Units'}
                    </div>
                  </div>
                </div>

                {/* Confidence Indicator */}
                <div className="col-span-2 flex items-center justify-between text-xs mt-2 px-1">
                  <span className="text-slate-500">AI Confidence:</span>
                  <span className={`font-medium ${extractedData.confidence >= 85 ? 'text-green-600' :
                    extractedData.confidence >= 50 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                    {extractedData.confidence}%
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-6 border-t border-slate-100 mt-6">
                <Button
                  onClick={handleConfirm}
                  disabled={!extractedData.quantity || !extractedData.start_date}
                  className="flex-1 bg-[#003B5C] text-white hover:bg-[#002A42]"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Apply Data
                </Button>
                <Button
                  onClick={() => {
                    setPreviewUrl(null);
                    setExtractedData(null);
                    setLowConfidence(false);
                  }}
                  variant="outline"
                  className="px-6"
                >
                  Rescan
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptScanner;
